// frontend/src/components/Layout/PublicHeader.tsx
// Enhanced public header with external links for public pages

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Menu, 
  X, 
  ChevronDown,
  User,
  LogOut,
  Settings,
  Calendar,
  MessageSquare,
  AlertCircle
} from 'lucide-react';

interface PublicHeaderProps {
  variant?: 'full' | 'minimal';
  showAuthButtons?: boolean;
  className?: string;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({ 
  variant = 'full',
  showAuthButtons = true,
  className = ''
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setIsUserMenuOpen(false);
      
      // Call logout function
      await logout();
      
      // Force navigation to home page after logout
      navigate('/', { replace: true });
      
      // Optional: Show success message (you can customize this)
      console.log('Logout successful');
      
    } catch (error) {
      console.error('Logout failed:', error);
      // Handle logout error - you might want to show a toast notification
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Check if current path is active
  const isActivePath = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Navigation items based on variant
  const getNavigationItems = () => {
    const fullNavigation = [
      { name: 'Home', href: '/', external: true },
      { name: 'Services', href: '/services', external: true },
      { name: 'Providers', href: '/providers', external: true },
      { name: 'How It Works', href: '/how-it-works', external: true },
      { name: 'About', href: '/about', external: true },
      { name: 'Contact', href: '/contact', external: true }
    ];

    const minimalNavigation = [
      { name: 'Home', href: '/', external: true },
      { name: 'Services', href: '/services', external: true },
      { name: 'About', href: '/about', external: true }
    ];

    return variant === 'minimal' ? minimalNavigation : fullNavigation;
  };

  const navigationItems = getNavigationItems();

  return (
    <header className={`bg-white shadow-sm sticky top-0 z-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <span className="text-xl font-bold text-gray-900">AtmyArea</span>
            </a>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigationItems.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    isActivePath(item.href)
                      ? 'text-blue-600 font-semibold'
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>
          
          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {showAuthButtons && (
              <>
                {isAuthenticated && user ? (
                  /* Authenticated User Menu */
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 px-3 py-2 rounded-lg transition-colors"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium">
                        {user.first_name || user.username}
                      </span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    
                    {isUserMenuOpen && (
                      <>
                        {/* Backdrop */}
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setIsUserMenuOpen(false)}
                        />
                        
                        {/* Dropdown Menu */}
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-20">
                          <div className="p-4 border-b border-gray-100">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {user.first_name || user.username}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="py-2">
                            <Link
                              to="/account"
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <Settings className="mr-3 h-4 w-4" />
                              Account Settings
                            </Link>
                            <Link
                              to="/account/bookings"
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <Calendar className="mr-3 h-4 w-4" />
                              My Bookings
                            </Link>
                            <Link
                              to="/account/messages"
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <MessageSquare className="mr-3 h-4 w-4" />
                              Messages
                            </Link>
                            <button
                              onClick={handleLogout}
                              disabled={isLoggingOut}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                              {isLoggingOut ? (
                                <>
                                  <div className="mr-3 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700"></div>
                                  Signing Out...
                                </>
                              ) : (
                                <>
                                  <LogOut className="mr-3 h-4 w-4" />
                                  Sign Out
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  /* Guest Auth Buttons */
                  <div className="flex items-center space-x-3">
                    <Link
                      to="/login"
                      className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register/customer"
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      Join as Customer
                    </Link>
                    <Link
                      to="/register/partner"
                      className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      Become a Provider
                    </Link>
                  </div>
                )}
              </>
            )}
            
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 p-2"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4">
            <div className="space-y-1">
              {/* Navigation Links */}
              {navigationItems.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                      isActivePath(item.href)
                        ? 'text-blue-600 bg-blue-50 font-semibold'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              
              {/* Mobile Auth Buttons */}
              {showAuthButtons && !isAuthenticated && (
                <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
                  <Link
                    to="/login"
                    className="block text-center bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg text-base font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register/customer"
                    className="block text-center bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg text-base font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Join as Customer
                  </Link>
                  <Link
                    to="/register/partner"
                    className="block text-center bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded-lg text-base font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Become a Provider
                  </Link>
                </div>
              )}

              {/* Mobile Authenticated User Menu */}
              {showAuthButtons && isAuthenticated && user && (
                <div className="border-t border-gray-200 pt-4 mt-4 space-y-1">
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Signed in as {user.first_name || user.username}
                  </div>
                  <Link
                    to="/account"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Account Settings
                  </Link>
                  <Link
                    to="/account/bookings"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Bookings
                  </Link>
                  <Link
                    to="/account/messages"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Messages
                  </Link>
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isLoggingOut ? 'Signing Out...' : 'Sign Out'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default PublicHeader;